import { ClientManager } from './ElasticAppSearchClient';
import mapRawResults from './MapRawResults';
import debounce from 'lodash/debounce';

/**
 * doQuery
 *
 * This is the callback function of doQuery action
 * that should trigger the querying of the elastic engine
 * using the filtered query object.
 *
 * @param {{ query: '', options: {} }} query
 */
const doQuery = debounce( ( query = { query: null } ) => {
	const isHeaderSearch = query?.isHeaderSearch || false;
	if ( isHeaderSearch ) {
		/**
		 * qalaElasticCore.filter.headerSearch.query
		 *
		 * This filter is the most important
		 * as it allow all the other “filter” blocks to apply their filters
		 * or the search block to apply its query
		 * just before the Elastic Core queries the elastic engine.
		 *
		 * Only runs for header search in Qala Elastic Search (New)
		 */
		query = wp.hooks.applyFilters( 'qalaElasticCore.filter.headerSearch.query', query );
	} else {
		/**
		 * qalaElasticCore.filter.query
		 *
		 * This filter is the most important
		 * as it allow all the other “filter” blocks to apply their filters
		 * or the search block to apply its query
		 * just before the Elastic Core queries the elastic engine.
		 */
		query = wp.hooks.applyFilters( 'qalaElasticCore.filter.query', query );
	}

	if ( isHeaderSearch ) {
		/**
		 * qalaElasticCore.action.headerSearch.beforeQuery
		 *
		 * This action will be fired just before a search query is sent to elastic.
		 *
		 * Only runs for header search in Qala Elastic Search (New)
		 */
		wp.hooks.doAction( 'qalaElasticCore.action.headerSearch.beforeQuery' );
	} else {
		/**
		 * qalaElasticCore.action.beforeQuery
		 *
		 * This action will be fired just before a search query is sent to elastic.
		 */
		wp.hooks.doAction( 'qalaElasticCore.action.beforeQuery' );
	}

	/**
	 * Using elastic search app client to send the query to the search engin.
	 */
	const clientManager = new ClientManager();
	clientManager.search( query.query, query.options )
		.then( resultList => {
			let results = [];
			if ( isHeaderSearch ) {
				/**
				 * qalaElasticCore.filter.headerSearch.query.results
				 *
				 * This filter allows the filtering of the raw results
				 * obtained from the elastic engine before the actions are fired.
				 *
				 * Only runs for header search in Qala Elastic Search (New)
				 */
				results = wp.hooks.applyFilters( 'qalaElasticCore.filter.headerSearch.query.results', mapRawResults( resultList.rawResults ) );
			} else {
				/**
				 * qalaElasticCore.filter.query.results
				 *
				 * This filter allows the filtering of the raw results
				 * obtained from the elastic engine before the actions are fired.
				 */
				results = wp.hooks.applyFilters( 'qalaElasticCore.filter.query.results', mapRawResults( resultList.rawResults ) );
			}

			if ( isHeaderSearch ) {
				/**
				 * qalaElasticCore.action.headerSearch.onResult.results
				 *
				 * This action will be fired once the result is received from elastic,
				 * and after it has been filtered.
				 * The parameter of the action will be an array of elastic results.
				 *
				 * Only runs for header search in Qala Elastic Search (New)
				 */
				wp.hooks.doAction( 'qalaElasticCore.action.headerSearch.onResult.results', results );
			} else {
				/**
				 * qalaElasticCore.action.onResult.results
				 *
				 * This action will be fired once the result is received from elastic,
				 * and after it has been filtered.
				 * The parameter of the action will be an array of elastic results.
				 */
				wp.hooks.doAction( 'qalaElasticCore.action.onResult.results', results );
			}

			if ( isHeaderSearch ) {
				/**
				 * qalaElasticCore.action.headerSearch.onResult.meta
				 *
				 * This action is fired at the same time that `qalaElasticCore.action.headerSearch.onResult.result` is fired.
				 * The parameter for this action is only the search results meta
				 * such as total number of results, number of pages, and current page )
				 *
				 * Only runs for header search in Qala Elastic Search (New)
				 */
				wp.hooks.doAction( 'qalaElasticCore.action.headerSearch.onResult.meta', resultList.rawInfo.meta );
			} else {
				/**
				 * qalaElasticCore.action.onResult.meta
				 *
				 * This action is fired at the same time that `qalaElasticCore.action.onResult.result` is fired.
				 * The parameter for this action is only the search results meta
				 * such as total number of results, number of pages, and current page )
				 */
				wp.hooks.doAction( 'qalaElasticCore.action.onResult.meta', resultList.rawInfo.meta );
			}

			if ( resultList.rawInfo.facets ) {
				if ( isHeaderSearch ) {
					/**
					 * qalaElasticCore.action.headerSearch.onResult.facets
					 *
					 * This action is fired at the same time that `qalaElasticCore.action.headerSearch.onResult.result` is fired.
					 * This returns all facets (so the count of how many documents from this query have each facet).
					 * This will only be set if the initial search request sends the `facet` key - this is used by
					 * Qala Elastic Filters (New)
					 *
					 * Only runs for header search in Qala Elastic Search (New)
					 */
					wp.hooks.doAction( 'qalaElasticCore.action.headerSearch.onResult.facets', resultList.rawInfo.facets );
				} else {
					/**
					 * qalaElasticCore.action.onResult.facets
					 *
					 * This action is fired at the same time that `qalaElasticCore.action.onResult.result` is fired.
					 * This returns all facets (so the count of how many documents from this query have each facet).
					 * This will only be set if the initial search request sends the `facet` key - this is used by
					 * Qala Elastic Filters (New)
					 */
					wp.hooks.doAction( 'qalaElasticCore.action.onResult.facets', resultList.rawInfo.facets );
				}
			}
		} )
		.catch( error => {
			if ( isHeaderSearch ) {
				/**
				 * qalaElasticCore.action.headerSearch.onResult.error
				 *
				 * This action to be fired if there is an error
				 * it will return the error parameter to be handled.
				 *
				 * Only runs for header search in Qala Elastic Search (New)
				 */
				wp.hooks.doAction( 'qalaElasticCore.action.headerSearch.onResult.error', error );
			} else {
				/**
				 * qalaElasticCore.action.onResult.error
				 *
				 * This action to be fired if there is an error
				 * it will return the error parameter to be handled.
				 */
				wp.hooks.doAction( 'qalaElasticCore.action.onResult.error', error );
			}

			console.error( error ); // eslint-disable-line no-console
		} );

	/**
	 * qalaElasticCore.filter.debounceQueryTimeout
	 *
	 * How long to debounce doQuery. This is an optimization to prevent duplicate queries in a short amount of time,
	 * for example when resetting filters. Should probably be in the range of 20-100.
	 */
}, wp.hooks.applyFilters( 'qalaElasticCore.filter.debounceQueryTimeout', 30 ) );

/**
 * qalaElasticCore.action.doQuery
 *
 * AddAction Hook to call the elastic search app and do the query.
 * It will doAction for the results and the Info meta.
 */
wp.hooks.addAction( 'qalaElasticCore.action.doQuery', 'qalaElasticCore', doQuery );

/**
 * Encode html special characters before sending the query to the elastic engine.
 */
wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'qalaElasticCoreGlobalQueryFilters', query => {
	if ( ! query.options || ! query.options.filters ) {
		return query;
	}

	[ 'all', 'any', 'none' ].forEach( filterType => {
		if ( ! query.options.filters[ filterType ] ) {
			return query;
		}

		for ( let index = 0; index < query.options.filters[ filterType ].length; index++ ) {
			const filter = query.options.filters[ filterType ][ index ];
			const key = Object.keys( filter )[ 0 ];

			// Check if filter is an array or string
			if ( Array.isArray( filter[ key ] ) ) {
				query.options.filters[ filterType ][ index ][ key ] = filter[ key ].map( value => {
					return value.replace( /&(?!amp;)/g, '&amp;' );
				} );
			} else if ( typeof filter[ key ] === 'string' ) {
				query.options.filters[ filterType ][ index ][ key ] = filter[ key ].replace( /&(?!amp;)/g, '&amp;' );
			}
		}
	} );

	return query;
}, 200 );

wp.hooks.addFilter( 'qalaElasticCore.filter.query', 'qalaElasticCoreRequiredFilters', query => appendRequiredFilters( query ), 300 );

/**
 * Appends required filters to "all" filters
 *
 * @param {Object} query
 * @return {Object} Query object.
 */
const appendRequiredFilters = query => {
	const requiredFilters = window.QalaElasticCore?.requiredFilters || [];
	requiredFilters.forEach( ( { key, value } ) => query.options.filters.all.push( { [ key ]: value } ) );
	return query;
};
